// 개선 사항 1: 모달이 꺼졌을 때 전체 그래프 다시 꽉 차게 보여지도록 처리
// 개선 사항 2: 2D 그래프 레이아웃을 계층적 정렬으로

import React, { useRef, useMemo, useState, useEffect } from "react";
import ForceGraph3D from "react-force-graph-3d";
import * as THREE from "three";
import { Graph3DNode, Graph3DLink, ForceGraphLink } from "../../types";
import { setSelectionRange } from "@testing-library/user-event/dist/utils";

interface Props {
  nodes: Graph3DNode[];
  links: Graph3DLink[];
  onNodeClick: (node: Graph3DNode) => Promise<boolean>;
  subGraph: {
    nodes: Graph3DNode[];
    links: Graph3DLink[];
  } | null;
  LAW_TYPES: { name: string; mstCode: string; color: string }[];
}

const edgeColor = "#ffffff";
const highLightEdgeColor = "#ff0000";
const nodeBaseColor = "#999999";
const LawGraph3D: React.FC<Props> = ({
  nodes,
  links,
  onNodeClick,
  subGraph,
  LAW_TYPES,
}) => {
  const fgRef = useRef<any>(null);

  const [highlightNode, setHighlightNode] = useState<string | null>(null);
  const [highlightLinks, setHighlightLinks] = useState<Set<string>>(new Set());
  const [highlightNodes, setHighlightNodes] = useState<Set<string>>(new Set());
  const zoomSelectedSubgraph = (matchedNodes: Graph3DNode[]) => {
    const positions = matchedNodes
      .filter(
        (n) =>
          typeof n.x === "number" &&
          typeof n.y === "number" &&
          typeof n.z === "number"
      )
      .map((n) => new THREE.Vector3(n.x!, n.y!, n.z!));

    if (positions.length > 0) {
      const center = positions
        .reduce((acc, cur) => acc.add(cur), new THREE.Vector3())
        .divideScalar(positions.length);
      const boundingBox = new THREE.Box3().setFromPoints(positions);
      const size = new THREE.Vector3();
      boundingBox.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);

      const camera = fgRef.current.camera();
      const fov = (camera.fov * Math.PI) / 180;
      const distance = maxDim / (2 * Math.tan(fov / 2));

      const newCameraPos = center
        .clone()
        .add(new THREE.Vector3(0, 0, distance * 1.5));

      fgRef.current.cameraPosition(newCameraPos, center, 1000);
    }
  };

  // Highlinghted Graph, Node (노드 클릭 시에 변경)
  useEffect(() => {
    if (subGraph?.links.length && subGraph?.links.length > 0) {
      const nodeSet = new Set(subGraph.nodes.map((n) => n.id));
      const linkSet = new Set(
        subGraph.links.map((l) => `${l.source_id}-${l.target_id}`)
      );
      setHighlightNodes(nodeSet);
      setHighlightLinks(linkSet);
      const matchedNodes = nodes.filter((n) => nodeSet.has(n.id));
      zoomSelectedSubgraph(matchedNodes);
    } else {
      setHighlightNodes(new Set());
      setHighlightLinks(new Set());
    }
  }, [nodes, subGraph]);

  const onSingleNodeClick = async (node: Graph3DNode) => {
    const hasSubGraph = await onNodeClick(node);
    setHighlightNode(node.id);
    if (!hasSubGraph) {
      const dist = 100;
      const distRatio =
        1 + dist / Math.hypot(node.x ?? 0, node.y ?? 0, node.z ?? 0);
      fgRef.current.cameraPosition(
        {
          x: (node.x ?? 0) * distRatio,
          y: (node.y ?? 0) * distRatio,
          z: (node.z ?? 0) * distRatio,
        },
        node,
        1000
      );
    }
  };
  const nodeIdSet = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);

  const processedLinks: ForceGraphLink[] = useMemo(() => {
    return links
      .map((link) => ({
        source: link.source_id,
        target: link.target_id,
        type: link.type ?? "",
        highlighted: highlightLinks.has(`${link.source_id}-${link.target_id}`),
      }))
      .filter(
        (link) => nodeIdSet.has(link.source) && nodeIdSet.has(link.target)
      );
  }, [links, nodeIdSet, highlightLinks]);

  const getCustomThreeNode = (node: Graph3DNode) => {
    const isHighlighted =
      highlightNodes.size === 0 || highlightNodes.has(node.id);

    const material = new THREE.MeshStandardMaterial({
      color: getBaseColorByLawName(node),
      opacity: isHighlighted ? 1 : 0.2,
      transparent: true,
    });

    const geometry = new THREE.SphereGeometry(4, 16, 16);
    const mesh = new THREE.Mesh(geometry, material);

    return mesh;
  };
  const getBaseColorByLawName = (node: Graph3DNode) => {
    const lawType = LAW_TYPES.find((law) => law.mstCode === node.mst_code);
    if (lawType) {
      return lawType.color;
    } else {
      return nodeBaseColor;
    }
  };
  const getNodeLabel = (node: Graph3DNode) => {
    return `${node.law_name ?? ""} ${node.number ?? ""}\n$${
      node.short_content ? node.short_content.slice(0, 100) : ""
    }...`;
  };
  const getLinkWidth = (link: ForceGraphLink) => {
    return link.highlighted ? 0.7 : 0.2;
  };
  const getLinkColor = (link: ForceGraphLink) => {
    return link.highlighted ? highLightEdgeColor : edgeColor;
  };

  return (
    <div className="absolute inset-0 w-full h-full">
      <ForceGraph3D
        ref={fgRef}
        graphData={{ nodes, links: processedLinks }}
        backgroundColor="#ffffff"
        linkDirectionalArrowLength={2.5}
        linkDirectionalArrowRelPos={1}
        nodeLabel={(node: Graph3DNode) => getNodeLabel(node)}
        linkWidth={(link) => getLinkWidth(link)}
        linkColor={(link) => getLinkColor(link)}
        nodeThreeObject={(node: Graph3DNode) => {
          return getCustomThreeNode(node);
        }}
        onNodeClick={onSingleNodeClick}
        warmupTicks={0}
      />
    </div>
  );
};

export default LawGraph3D;
